<script setup lang="ts">
import { computed } from 'vue'

import { DateRangeControl, valueFromSelection, type DateRangeSelection } from '@/shared/date-range'
import { useI18n } from '@/shared/i18n'
import { getCompareDateRange } from '@/shared/lib/date/range'
import { toApiDate } from '@/shared/lib/date/toApiDate'
import {
  type CompareMode,
  type DatePickerProps,
  type DatePreset,
  type DateRangeValue,
  type MonthLabels,
  type WeekdayLabels,
} from '@/shared/ui'

const emit = defineEmits<{ change: [postData: string] }>()
const { t } = useI18n()

function startOfDay(date: Date): Date {
  const next = new Date(date)
  next.setHours(0, 0, 0, 0)
  return next
}

function initialValue(): DateRangeValue {
  const today = startOfDay(new Date())
  const end = new Date(today)
  end.setDate(end.getDate() - 1)
  const start = new Date(today)
  start.setDate(start.getDate() - 28)
  const compare = getCompareDateRange(start, end)
  return {
    start,
    end,
    presetId: 'Last 28 Days',
    compareEnabled: true,
    compareType: 'Previous Period',
    compareStart: compare.compareStart,
    compareEnd: compare.compareEnd,
  }
}

function serialize(value: DateRangeValue): string {
  const params = new URLSearchParams()
  if (value.start) params.set('date_start', toApiDate(value.start))
  if (value.end) params.set('date_end', toApiDate(value.end))
  if (value.compareEnabled && value.compareStart) {
    params.set('compare_date_start', toApiDate(value.compareStart))
  }
  if (value.compareEnabled && value.compareEnd) {
    params.set('compare_date_end', toApiDate(value.compareEnd))
  }
  return `&${params.toString()}`
}

const presets = computed<DatePreset[]>(() => [
  { id: 'Yesterday', label: t('summary.datePicker.presets.yesterday') },
  { id: 'This Week', label: t('summary.datePicker.presets.thisWeek') },
  { id: 'Last Week', label: t('summary.datePicker.presets.lastWeek') },
  { id: 'This Month', label: t('summary.datePicker.presets.thisMonth') },
  { id: 'Last Month', label: t('summary.datePicker.presets.lastMonth') },
  { id: 'This Year', label: t('summary.datePicker.presets.thisYear') },
  { id: 'Last Year', label: t('summary.datePicker.presets.lastYear') },
  { id: 'Last 7 Days', label: t('summary.datePicker.presets.last7Days') },
  { id: 'Last 14 Days', label: t('summary.datePicker.presets.last14Days') },
  { id: 'Last 28 Days', label: t('summary.datePicker.presets.last28Days') },
  { id: 'Last 30 Days', label: t('summary.datePicker.presets.last30Days') },
  { id: 'Last 60 Days', label: t('summary.datePicker.presets.last60Days') },
  { id: 'Last 90 Days', label: t('summary.datePicker.presets.last90Days') },
  { id: 'Maximum', label: t('summary.datePicker.presets.maximum') },
  { id: 'Custom', label: t('summary.datePicker.presets.custom') },
])
const compareModes = computed<CompareMode[]>(() => [
  { id: 'Previous Period', label: t('summary.datePicker.compare.previousPeriod') },
  { id: 'Previous Week', label: t('summary.datePicker.compare.previousWeek') },
  { id: 'Previous Month', label: t('summary.datePicker.compare.previousMonth') },
  { id: 'Previous Year', label: t('summary.datePicker.compare.previousYear') },
])
const weekdayLabels = computed<WeekdayLabels>(() => [
  t('summary.datePicker.weekdays.mon'),
  t('summary.datePicker.weekdays.tue'),
  t('summary.datePicker.weekdays.wed'),
  t('summary.datePicker.weekdays.thu'),
  t('summary.datePicker.weekdays.fri'),
  t('summary.datePicker.weekdays.sat'),
  t('summary.datePicker.weekdays.sun'),
])
const monthLabels = computed<MonthLabels>(() => [
  t('summary.datePicker.months.jan'),
  t('summary.datePicker.months.feb'),
  t('summary.datePicker.months.mar'),
  t('summary.datePicker.months.apr'),
  t('summary.datePicker.months.may'),
  t('summary.datePicker.months.jun'),
  t('summary.datePicker.months.jul'),
  t('summary.datePicker.months.aug'),
  t('summary.datePicker.months.sep'),
  t('summary.datePicker.months.oct'),
  t('summary.datePicker.months.nov'),
  t('summary.datePicker.months.dec'),
])

const picker = computed<Omit<DatePickerProps, 'min' | 'max'>>(() => ({
  compact: true,
  presets: presets.value,
  compareModes: compareModes.value,
  weekdayLabels: weekdayLabels.value,
  monthLabels: monthLabels.value,
  triggerLabel: t('summary.datePicker.trigger'),
  emptyLabel: t('summary.datePicker.empty'),
  rangeSeparator: t('summary.datePicker.rangeSeparator'),
  compareSeparator: t('summary.datePicker.compareSeparator'),
  compareToggleLabel: t('summary.datePicker.compareToggle'),
  startInputLabel: t('summary.datePicker.start'),
  endInputLabel: t('summary.datePicker.end'),
  compareStartInputLabel: t('summary.datePicker.compareStart'),
  compareEndInputLabel: t('summary.datePicker.compareEnd'),
  startPlaceholder: t('summary.datePicker.start'),
  endPlaceholder: t('summary.datePicker.end'),
  compareStartPlaceholder: t('summary.datePicker.compareStart'),
  compareEndPlaceholder: t('summary.datePicker.compareEnd'),
  resetLabel: t('summary.datePicker.reset'),
  applyLabel: t('summary.datePicker.apply'),
}))

function change(selection: DateRangeSelection): void {
  emit('change', serialize(valueFromSelection(selection)))
}

defineExpose({ initialPostData: serialize(initialValue()) })
</script>

<template>
  <DateRangeControl
    id="summary"
    :default-value="initialValue()"
    :picker="picker"
    @change="change"
  />
</template>
